import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Lottie from 'react-lottie';
import animationData from './../../assets/lotties/bcn.json';
import { motion, AnimatePresence } from 'framer-motion';

const DropdownMenu = ({ active, menu }) => {
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef(null);

  // Animation effect when menu changes
  useEffect(() => {
    if (active === menu) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [active, menu]);

  if (active !== menu) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="mt-6 w-full md:block hidden"
          ref={menuRef}
        >
          <Container>
            <motion.div 
              className="w-full xl:w-[960px] mx-auto rounded-2xl backdrop-blur-xl bg-white/15 border border-white/20 shadow-2xl overflow-hidden"
              style={{
                backdropFilter: 'blur(24px)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2), 0 0 20px rgba(255, 255, 255, 0.05) inset'
              }}
              whileHover={{ boxShadow: '0 15px 50px rgba(0, 0, 0, 0.25), 0 0 25px rgba(255, 255, 255, 0.07) inset' }}
            >
              <div className="p-8">
                {menu === "company" && (
                  <div className="w-full">
                    <h4 className="text-white text-xl font-medium mb-6 border-b border-white/15 pb-3 flex items-center">
                      <span className="mr-2">Company</span>
                      <span className="bg-teal-400/20 text-teal-200 text-xs py-1 px-2 rounded-full">Explore</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <CompanyLink href="/about-futeur" title="About" icon="iccs (1).svg" description="Learn about our story" />
                      <CompanyLink href="/careers" title="Careers" icon="iccs (2).svg" description="Join our team" />
                      <CompanyLink href="/legal" title="Legal" icon="iccs (3).svg" description="Terms and policies" />
                      <CompanyLink href="/vision" title="Vision" icon="vision.svg" description="Our future plans" />
                      <CompanyLink href="/contact" title="Contact" icon="phone.svg" description="Get in touch" />
                    </div>
                  </div>
                )}
                
                {menu === "products" && (
                  <div className="w-full">
                    <h4 className="text-white text-xl font-medium mb-6 border-b border-white/15 pb-3 flex items-center">
                      <span className="mr-2">Products</span>
                      <span className="bg-teal-400/20 text-teal-200 text-xs py-1 px-2 rounded-full">Featured</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <ProductCard href="/FuteurCred" title="FuteurCred" videoSrc="bcn.json" description="Next-gen blockchain solution" />
                      {/* You can add more products here */}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CompanyLink = ({ href, title, icon, description }) => (
  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
    <Link
      to={href}
      className="flex items-center justify-between px-4 py-5 text-white rounded-xl backdrop-blur-lg bg-black/30 hover:bg-black/40 border border-white/10 hover:border-teal-300/30 transition-all duration-300 group h-full"
    >
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-teal-500/20 to-blue-500/10 flex items-center justify-center group-hover:from-teal-400/30 group-hover:to-blue-400/20 transition-all duration-300 shadow-lg shadow-teal-900/10">
          <img
            src={`/images/${icon}`}
            alt={title}
            className="w-7 h-7 object-contain brightness-110"
          />
        </div>
        <div>
          <p className="font-medium text-lg transition-colors duration-300 text-white group-hover:text-teal-200">{title}</p>
          <p className="text-white/60 text-sm mt-1">{description}</p>
        </div>
      </div>
      <div className="flex-shrink-0 bg-black/30 rounded-full p-2 group-hover:bg-teal-500/20 transition-all duration-300">
        <svg 
          className="w-5 h-5 text-teal-300 transform group-hover:translate-x-1 transition-all duration-300" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  </motion.div>
);

const ProductCard = ({ href, title, videoSrc, description }) => (
  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
    <Link
      to={href}
      className="block overflow-hidden rounded-xl backdrop-blur-lg bg-black/30 hover:bg-black/40 border border-white/10 hover:border-teal-300/30 transition-all duration-300 group h-full"
    >
      <div className="p-5">
        <div className="w-full h-48 rounded-lg bg-gradient-to-br from-gray-900/80 to-gray-800/80 flex items-center justify-center mb-5 overflow-hidden transition-all duration-500 border border-white/5">
          {videoSrc && videoSrc.endsWith(".json") ? (
            <div className="scale-110 group-hover:scale-125 transition-all duration-700">
              <Lottie
                options={{
                  animationData: animationData,
                  autoplay: true,
                  loop: true,
                  rendererSettings: {
                    preserveAspectRatio: "xMidYMid slice"
                  }
                }}
                height={180}
                width={180}
              />
            </div>
          ) : videoSrc && videoSrc.endsWith(".gif") ? (
            <img
              src={`/videos/${videoSrc}`}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
            />
          ) : videoSrc ? (
            <video
              src={`/videos/${videoSrc}`}
              className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
              autoPlay
              loop
              muted
            />
          ) : null}
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-lg text-white group-hover:text-teal-200 transition-colors duration-300">{title}</h3>
            <p className="text-sm text-white/60 mt-1">{description}</p>
            <div className="flex items-center mt-3">
              <span className="text-xs text-teal-300">Explore features</span>
              <svg 
                className="w-4 h-4 text-teal-300 transform group-hover:translate-x-1 transition-all duration-300 ml-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
          </div>
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-teal-500/20 to-blue-500/10 flex items-center justify-center group-hover:from-teal-400/30 group-hover:to-blue-400/20 transition-all duration-300 shadow-lg shadow-teal-900/10">
            <svg 
              className="w-5 h-5 text-teal-300 transform group-hover:translate-x-1 transition-all duration-300" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  </motion.div>
);

const FeatureLink = ({ href, title, icon, mockup }) => (
  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
    <Link to={href} className="block h-full group">
      <div className="w-full h-48 overflow-hidden rounded-xl relative transition-all duration-300 backdrop-blur-lg bg-black/30 hover:bg-black/40 border border-white/10 hover:border-teal-300/30">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-500/5 to-transparent"></div>
        <img 
          src={`/images/${icon}`} 
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-20 h-20 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 brightness-110" 
          alt="" 
        />
        <img 
          src={`/images/${mockup}`} 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 max-h-36 transition-all duration-500 group-hover:translate-y-1 group-hover:scale-105" 
          alt="" 
        />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <p className="font-medium text-white group-hover:text-teal-200 transition-colors duration-300">{title}</p>
        <div className="flex-shrink-0 bg-black/30 rounded-full p-2 group-hover:bg-teal-500/20 transition-all duration-300">
          <svg 
            className="w-5 h-5 text-teal-300 transform group-hover:translate-x-1 transition-all duration-300" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>
      </div>
    </Link>
  </motion.div>
);

export default DropdownMenu;